/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { BUTTON_HEIGHT, FONT_SIZE, FONT_SIZE_VALUE } from "@lhd/constants";
import { DISPLAY_SIZES } from "@lhd/types";
import {
  ColDef,
  ColumnMovedEvent,
  GridApi,
  GridSizeChangedEvent,
  RowHeightParams,
} from "ag-grid-community";
import { get } from "lodash";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { getDisplaySize } from "../DisplaySizeSelector/selector";
import CellTextDefault from "./components/CellDefault";
import HeaderDefault from "./components/HeaderDefault";
import HeaderFilter from "./components/HeaderFilter";
import useFilterPanel from "./useFilterPanel";
import { FilterInfo } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/AppSheetComponents/FilterIcon";
import { useSearchParams } from "react-router-dom";
import { TypeEditInfo } from "../TableForm/types";
export type ColumnDef = {
  headerName: string;
  field?: string;
  minWidth?: number;
  maxWidth?: number;
  width?: number;
  sortable?: boolean;
  cellStyle?: any;
  suppressMovable?: boolean;
  children?: ColumnDef[];
  headerWithFilter?: boolean;
  headerComponentFramework?: any;
  editInfo?: TypeEditInfo;
  getRowIdForForm?: (data: any) => any;
  lessThanField?: string;
  greaterThanField?: string;
  options?: any[];
};

export type ReceivedProps = {
  // ===== CORE =====
  gridRef: any;
  gridApi: GridApi;
  rowData: Record<string, any>[];
  columnDefs: ColumnDef[];
  defaultColDef?: any;
  loading: boolean;

  // ===== GRID EVENTS =====
  onGridReady: (params: GridSizeChangedEvent) => void;
  onGridSizeChanged?: (e: GridSizeChangedEvent) => void;
  onColumnResized?: (e?: any) => void;
  onColumnMoved?: (e: ColumnMovedEvent<any>) => void;
  onDragStopped?: (e?: any) => void;

  // ===== ROW EVENTS =====
  onRowClicked?: (e: any) => void;
  onSelectionChanged?: (e: any) => void;
  onRowDragEnd?: (e: any) => void;

  // ===== ROW FUNCTIONS =====
  getRowClass?: (params: any) => string;
  getRowHeight?: (params: RowHeightParams<any>) => number | undefined | null;
  getRowId?: (params: any) => string;

  // ===== FILTER =====
  onFilterUpdate?: any;
  clearFilter?: () => void;
  onSortChanged?: (e?: unknown) => void;
  suppressMenuHide?: boolean;
  agLocaleText?: Record<string, string>;

  // ===== PAGINATION =====
  page?: any;
  total?: any;
  pageSize?: any;
  setPage?: any;
  setPageSize?: any;
  isCustomPagination?: boolean;
  showPagination?: boolean;
  isShowPaginationHeader?: boolean;

  // ===== LAYOUT / STYLE =====
  gridStyle?: { width?: string; height?: string };
  tableHeight?: number | string;
  autoFitRemainingHeight?: boolean;
  rowHeight?: number;
  headerHeight?: number;
  floatingFiltersHeight?: number;
  scrollbarWidth?: number;
  styleVerticalScrollBar?: React.CSSProperties;
  domLayout?: "autoHeight" | "normal" | "print";

  // ===== BEHAVIOR FLAGS =====
  rowSelection?: "single" | "multiple";
  rowDragManaged?: boolean;
  animateRows?: boolean;
  alwaysShowVerticalScroll?: boolean;
  noFitColumns?: boolean;
  singleClickEdit?: boolean;
  suppressRowHoverHighlight?: boolean;
  suppressRowClickSelection?: boolean;
  suppressMovableColumns?: boolean;

  // ===== CUSTOM HEADER =====
  headerChildren?: any;
  preHeaderChildren?: any;

  // ===== BUTTONS =====
  isShowBtnCreate?: boolean;
  isShowButtonEnd?: boolean;
  onCreate?: () => void;
  btnCreateHeight?: number;
  // ===== OTHERS =====
  setIsOpenQuickView?: React.Dispatch<React.SetStateAction<boolean>>;
  context?: any;
  onChangeOpenFilter?: (filterInfo: FilterInfo) => void;
  filterInfo?: any;
  onBtnGroupHeightChange?: (height: number) => void;
  firstColumnName?: string;
  firstColumnWidth?: number;
  hiddenPaginationInURL?: boolean;
  colorHeader?: string;
  bgHeader?: string;
  isEditMode?: boolean;
  elementBetweenHeaderAndTable?: ReactNode;
  disabledPagination?: boolean;
};

export const ROW_HEIGHT = 45;
export const HEADER_HEIGHT = 60;

const useTable = (props: ReceivedProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [widthScrollVertical, setWidthScrollVertical] = useState(0);
  const [heightScrollHorizontal, setHeightScrollHorizontal] = useState(0);
  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;
  const height = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;
  const size = FONT_SIZE_VALUE[displaySize ?? DISPLAY_SIZES.SMALL];
  const { filterInfo, handleSetFilterInfo } = useFilterPanel({
    onChangeOpenFilter: props.onChangeOpenFilter,
    filterInfo: props.filterInfo,
  });
  const refTimeout = useRef<NodeJS.Timeout | null>(null);
  const gridDivRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const btnGroupRef = useRef<HTMLDivElement>(null);
  const [remainingTableHeight, setRemainingTableHeight] = useState(489);

  useEffect(() => {
    if (!props.autoFitRemainingHeight) return;

    const updateRemainingTableHeight = () => {
      if (!tableContainerRef.current) return;

      const { top } = tableContainerRef.current.getBoundingClientRect();
      const nextHeight = Math.max(
        Math.floor(document.documentElement.clientHeight - top - 1),
        0,
      );

      setRemainingTableHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight,
      );
    };

    updateRemainingTableHeight();
    window.addEventListener("resize", updateRemainingTableHeight);

    const resizeObserver = new ResizeObserver(updateRemainingTableHeight);
    if (gridDivRef.current) resizeObserver.observe(gridDivRef.current);

    return () => {
      window.removeEventListener("resize", updateRemainingTableHeight);
      resizeObserver.disconnect();
    };
  }, [props.autoFitRemainingHeight]);

  const containerStyle = useMemo(
    () => ({
      width: "100%",
      height: props.autoFitRemainingHeight
        ? remainingTableHeight
        : props.tableHeight ?? 489,
    }),
    [props.autoFitRemainingHeight, props.tableHeight, remainingTableHeight],
  );
  const gridStyle = useMemo(
    () => ({ height: "100%", width: "100%", ...props.gridStyle }),
    [props.gridStyle],
  );
  const scrollContainerBarVerticalRef = useRef<HTMLDivElement>(null);
  const scrollContainerBarHorizontalRef = useRef<HTMLDivElement>(null);
  const [columnApi, setColumnApi] = useState<any>(null);

  const defaultColDef = useMemo<ColDef | undefined>(() => {
    if (props.defaultColDef === null) return undefined;
    return {
      flex: 1,
      editable: false,
      resizable: false,
      floatingFilter: true,
      sortable: true,
      ...(props.defaultColDef ?? {}),
    };
  }, [props.defaultColDef]);

  const actionsColWidth = useMemo(() => {
    const BASE_WIDTH = props?.firstColumnWidth || 40;
    const EXTRA_WIDTH_PER_DIGIT = 8;
    const MAX_INDEX_DIGITS_AT_BASE_WIDTH = 2;
    if (!props?.rowData?.length) return BASE_WIDTH;
    const maxRowIndex = props.rowData.reduce(
      (max, _, index) =>
        Math.max(max, index + 1 + (props.page - 1) * props.pageSize),
      0,
    );
    const digits = String(maxRowIndex).length;
    const extraDigits = Math.max(0, digits - MAX_INDEX_DIGITS_AT_BASE_WIDTH);
    return BASE_WIDTH + extraDigits * EXTRA_WIDTH_PER_DIGIT;
  }, [props.rowData, props.page, props.pageSize, props.firstColumnWidth]);

  const columnDefs = useMemo(() => {
    const scrollCol = {
      headerName: "",
      field: "__scroll_bar_vertical__",
      flex: 0,
      minWidth: widthScrollVertical,
      maxWidth: widthScrollVertical,
      width: widthScrollVertical,
      sortable: false,
      suppressMovable: false,
      children: undefined,
    };

    const applyDefaultCellRenderer = (col: any) => {
      if (!col.cellRenderer) {
        col.cellRenderer = CellTextDefault;
      }
    };

    const columnDefsWithFilter = props.columnDefs.map((column: any) => {
      if (column.headerWithFilter) {
        column.headerComponentFramework = HeaderFilter;
      } else if (!column.headerComponentFramework) {
        column.headerComponentFramework = HeaderDefault;
      }
      applyDefaultCellRenderer(column);
      column.children?.forEach((child: any) => {
        if (child.headerWithFilter) {
          child.headerComponentFramework = HeaderFilter;
        } else if (!child.headerComponentFramework) {
          child.headerComponentFramework = HeaderDefault;
        }
        applyDefaultCellRenderer(child);
      });
      if (column.field === "index") {
        column.minWidth = actionsColWidth;
        column.cellRenderer = (params: any) => {
          const index =
            params.node.rowIndex + 1 + (props.page - 1) * props.pageSize;
          return <p className="orderNumber">{index}</p>;
        };
      }
      if (column.field === props.firstColumnName) {
        return {
          ...column,
        };
      }
      return column;
    });

    return [...columnDefsWithFilter, scrollCol];
  }, [
    props.columnDefs,
    widthScrollVertical,
    props.page,
    props.pageSize,
    actionsColWidth,
    props.firstColumnName,
    displaySize,
  ]);

  const totalCellHeader = useMemo(() => {
    return columnDefs.reduce((accumulator, currentValue) => {
      const current = currentValue?.children?.length || 1;
      return (accumulator += current);
    }, 0);
  }, [columnDefs]);

  const gridRef = props.gridRef;
  const handleShowVerticalScrollBar = useCallback(() => {
    clearTimeout(refTimeout.current as NodeJS.Timeout);
    refTimeout.current = setTimeout(() => {
      const eGridDiv =
        gridRef?.current?.api?.context?.contextParams?.providedBeanInstances
          ?.eGridDiv;

      if (eGridDiv) {
        // case horizontal scroll
        const header = eGridDiv.querySelector(".ag-header") as HTMLDivElement;
        if (scrollContainerBarHorizontalRef.current && header) {
          const { offsetHeight } = header;
          scrollContainerBarHorizontalRef.current.style.top = `${offsetHeight}px`;
          if (offsetHeight === 0) {
            scrollContainerBarHorizontalRef.current.style.position = "static";
          } else {
            scrollContainerBarHorizontalRef.current.style.position = "absolute";
          }
        }
        // case vertical scroll
        const agBodyViewport = (eGridDiv as HTMLDivElement).querySelector(
          ".ag-body-viewport",
        ) as HTMLDivElement;

        if (agBodyViewport) {
          const pinLeftEle = agBodyViewport.querySelector(
            ".ag-pinned-left-cols-container",
          ) as HTMLDivElement;
          if (pinLeftEle && scrollContainerBarVerticalRef.current) {
            const widthPinLeftElm = pinLeftEle.offsetWidth;

            scrollContainerBarVerticalRef.current.style.position = "absolute";
            scrollContainerBarVerticalRef.current.style.left =
              widthPinLeftElm - 1 + "px";
          } else if (scrollContainerBarVerticalRef.current) {
            scrollContainerBarVerticalRef.current.style.position = "static";
          }
        }
      }
    }, 10);
  }, [gridRef]);

  const handleFilterUpdate = useCallback(() => {
    props.onFilterUpdate?.();
    const next = new URLSearchParams(searchParams);
    if (!props.hiddenPaginationInURL) {
      next.set("page", "1");
      setSearchParams(next, { replace: true });
    }
  }, [props.onFilterUpdate]);
  const handleChangePageSize = (newPageSize: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", "1");
    next.set("per_page", String(newPageSize));
    setSearchParams(next, { replace: true });
  };
  const handleChangePage = (newPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(newPage));
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!columnApi) return;
    columnApi.setColumnWidth(props.firstColumnName || "index", actionsColWidth);
  }, [columnApi, actionsColWidth, props.firstColumnName]);

  useEffect(() => {
    if (!props.gridApi) return;

    const resize = () => {
      handleShowVerticalScrollBar();
      if (!props?.noFitColumns) {
        props.gridApi.sizeColumnsToFit();
      }
    };

    // resize container (tab, drawer, collapse...)

    let ro: ResizeObserver | null = null;
    let lastWidth: number | null = null;
    if (gridDivRef?.current) {
      ro = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width;
        if (lastWidth !== null && width === lastWidth) return;
        lastWidth = width;
        resize();
      });
      ro.observe(gridDivRef.current);
    }

    setTimeout(resize, 0);

    return () => {
      ro?.disconnect();
    };
  }, [props.gridApi, props.columnDefs, handleShowVerticalScrollBar]);

  useEffect(() => {
    if (!props.onBtnGroupHeightChange || !btnGroupRef.current) return;

    const reportHeight = () =>
      props.onBtnGroupHeightChange?.(btnGroupRef.current?.offsetHeight ?? 0);

    reportHeight();
    const ro = new ResizeObserver(reportHeight);
    ro.observe(btnGroupRef.current);

    return () => {
      ro.disconnect();
    };
  }, [props.onBtnGroupHeightChange]);

  const isFullWidthRow = useCallback(
    (params) => params.rowNode.data.fullWidth,
    [],
  );
  const onVirtualColumnsChanged = () => {
    //remove filter for checkbox colÏ
    document.getElementById("ag-20-input")?.remove();
    // document.querySelectorAll(".ag-text-field-input")[0].remove();
    const element = document.querySelectorAll(".ag-text-field-input");

    element.forEach((item: any) => {
      item.placeholder = "検索";
    });
  };

  const handleGridSizeChanged = async (params: GridSizeChangedEvent) => {
    props?.onGridReady?.(params);
    setColumnApi(params.columnApi ?? params.api);
  };

  const memoizedContext = useMemo(
    () => ({
      filterInfo,
      handleSetFilterInfo,
      gridApi: props.gridApi,
      ...(props.context || {}),
    }),
    [filterInfo, handleSetFilterInfo, props.gridApi, props.context],
  );

  useEffect(() => {
    handleShowVerticalScrollBar();
  });

  useEffect(() => {
    if (props.gridApi) {
      if (!props?.noFitColumns) {
        props.gridApi.sizeColumnsToFit();
      }

      if (props.loading) {
        props.gridApi.showLoadingOverlay();
      } else {
        if (!props.rowData.length) {
          props.gridApi.showNoRowsOverlay();
        } else {
          props.gridApi.hideOverlay();
        }
      }
    }
  }, [props.gridApi, props.loading, props?.noFitColumns, props.rowData.length]);

  useEffect(() => {
    const element = document.querySelectorAll(".ag-text-field-input");
    element.forEach((item: any) => {
      item.placeholder = "検索";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.gridApi, props.rowData]);

  const gridApi = props.gridApi as any;

  const offsetHeighteBodyViewport = get(
    gridApi,
    "gridBodyCtrl.eBodyViewport.offsetHeight",
    0,
  );
  const rowHeight = props.rowHeight || ROW_HEIGHT;
  return {
    ...props,
    context: memoizedContext,
    columnDefs,
    // Auto-enable vertical scroll when total row height exceeds viewport
    alwaysShowVerticalScroll:
      Boolean(props.rowData?.length) &&
      Boolean(offsetHeighteBodyViewport) &&
      props.rowData.length * rowHeight > offsetHeighteBodyViewport,

    containerStyle,
    gridStyle,
    defaultColDef,
    isFullWidthRow,
    onVirtualColumnsChanged,
    gridDivRef,
    tableContainerRef,
    btnGroupRef,
    // Safe getRowId fallback
    getRowId: props.getRowId || ((params: any) => params?.data?.id),
    handleShowVerticalScrollBar,
    widthScrollVertical,
    setWidthScrollVertical,
    scrollContainerBarVerticalRef,
    scrollContainerBarHorizontalRef,
    totalCellHeader,
    heightScrollHorizontal,
    setHeightScrollHorizontal,
    displaySize,
    height,
    size,
    filterInfo,
    handleSetFilterInfo,
    onGridReady: handleGridSizeChanged,
    handleFilterUpdate,
    handleChangePageSize,
    handleChangePage,
  };
};

export type Props = ReturnType<typeof useTable>;

export default useTable;
